export const DEFAULT_RATIO = "467 / 721";

// Responsive font helper:
// boxClamp(minPx, scale, maxPx)
// - minPx: smallest allowed font size
// - scale: responsive growth based on the image box
// - maxPx: kept for backward-compatible call sites, but we intentionally
//   avoid hard-capping overlay text so it keeps scaling with the template.
export const boxClamp = (minPx, scale, maxPx) =>
    `max(${minPx}px, min(${scale}cqw, ${scale}cqh))`;

export const FONT_PRESETS = {
    xs: boxClamp(5, 2.4, 13),
    sm: boxClamp(6, 3, 16),
    md: boxClamp(7, 3.35, 18),
    lg: boxClamp(8, 4.2, 22),
    xl: boxClamp(10, 4.8, 24),
    xxl: boxClamp(12, 5.6, 28),
    xxxl: boxClamp(14, 6.4, 32),
};

export const FONT_SIZES = {
    post: FONT_PRESETS.xl,
    lo: FONT_PRESETS.xl,
    large: boxClamp(8, 3.4, 18),
    kp: boxClamp(9, 3.8, 22),
};

export const COLORS = {
    black: "#010101",
    white: "#ffffff",
    red: "#be0107",
    blue: "#081f5f",
    brightRed: "#be0107",
    darkRed: "#C10000",
    pureRed: "#FE0000",
    orangeGold: "#FFC000",
    yellow: "#FFFF01",
    lightGreen: "#92D14F",
    green: "#00AF50",
    cyanLightBlue: "#01B0F1",
    brightBlue: "#0070C2",
    darkBlueNavy: "#01215E",
    purple: "#7030A0",
};

const VNVOGUE_FONT_FAMILY = '"VnVogue", "Arial Black", Arial, sans-serif';
const DEFAULT_PLATFORM_ADJUST = {
    mac: {},
    windows: {},
    other: {},
};
const withPlatformAdjust = (style = {}, platformAdjust) => ({
    ...style,
    platformAdjust: {
        ...DEFAULT_PLATFORM_ADJUST,
        ...(style.platformAdjust || {}),
        ...(platformAdjust || {}),
    },
});
const MC_TOPBAR_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "0em", letterSpacing: "0em" },
};
const MC_POST_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.11em", letterSpacing: "0em" },
};
const MC_LO_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "0em", letterSpacing: "0em" },
};
const MC_A_GROUP_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.09em", letterSpacing: "0em" },
};
const MT_TOPBAR_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.05em", letterSpacing: "0em" },
};
const MT_POST_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.09em", letterSpacing: "0em" },
};
const MT_LO_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "0em", letterSpacing: "0em" },
};
const MT_A_GROUP_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "0em", letterSpacing: "0em" },
};
const MT_NIGHT_TOPBAR_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.05em", letterSpacing: "0em" },
};
const MT_NIGHT_POST_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.1em", letterSpacing: "0em" },
};
const MT_NIGHT_LO_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "0em", letterSpacing: "0em" },
};
const MT_NIGHT_A_GROUP_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.09em", letterSpacing: "0em" },
};
const SC_TOPBAR_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "-0.01em" },
    windows: { offsetY: "-0.04em", letterSpacing: "0em" },
};
const SC_MAIN_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "-0.01em" },
    windows: { offsetY: "-0.12em", letterSpacing: "0em" },
};
const TC_TOPBAR_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.05em", letterSpacing: "0em" },
};
const TC_POST_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.11em", letterSpacing: "0em" },
};
const TC_LO_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.07em", letterSpacing: "0em" },
};
const TC_A_GROUP_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.05em", letterSpacing: "0em" },
};
const KH_TOPBAR_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.15em", letterSpacing: "0em" },
};
const KH_MAIN_PLATFORM_ADJUST = {
    mac: { offsetY: "0em", letterSpacing: "0em" },
    windows: { offsetY: "-0.15em", letterSpacing: "0em" },
};
const KP_DAY_PLATFORM_ADJUST = {
    topbar: {
        mac: { offsetY: "0em", letterSpacing: "0em" },
        windows: { offsetY: "-0.13em", letterSpacing: "0em" },
    },
    afi: {
        mac: { offsetY: "0em", letterSpacing: "0em" },
        windows: { offsetY: "-0.12em", letterSpacing: "0em" },
    },
    postGroup: {
        mac: { offsetY: "0em", letterSpacing: "0em" },
        windows: { offsetY: "0em", letterSpacing: "0em" },
    },
    loGroup: {
        mac: { offsetY: "0em", letterSpacing: "0em" },
        windows: { offsetY: "-0.06em", letterSpacing: "0em" },
    },
};
const KP_NIGHT_PLATFORM_ADJUST = {
    topbar: {
        mac: { offsetY: "0em", letterSpacing: "0em" },
        windows: { offsetY: "0em", letterSpacing: "0em" },
    },
    post: {
        mac: { offsetY: "0em", letterSpacing: "0em" },
        windows: { offsetY: "-0.12em", letterSpacing: "0em" },
    },
    lo: {
        mac: { offsetY: "0em", letterSpacing: "0em" },
        windows: { offsetY: "-0.1em", letterSpacing: "0em" },
    },
    aGroup: {
        mac: { offsetY: "0em", letterSpacing: "0em" },
        windows: { offsetY: "-0.1em", letterSpacing: "0em" },
    },
};

const createTextStyle = ({
    date = { fontSize: boxClamp(8, 3.4, 19), fontWeight: 500 },
    time = { fontSize: boxClamp(8, 3.4, 19), fontWeight: 500 },
    postRow = {
        twoDColor: COLORS.black,
        threeDColor: COLORS.black,
        fourDColor: COLORS.black,
        fontSize: FONT_SIZES.post,
        fontWeight: 500,
    },
    lo2d = { textColor: COLORS.blue, fontSize: FONT_SIZES.lo, fontWeight: 500 },
    lo3d = { textColor: COLORS.red, fontSize: FONT_SIZES.lo, fontWeight: 500 },
    aGroup = { twoDColor: COLORS.black, threeDColor: COLORS.black, fontSize: FONT_SIZES.large, fontWeight: 500 },
    afi,
    kpPostGroup,
    kpLoGroup,
} = {}) => ({
    date: withPlatformAdjust(date),
    time: withPlatformAdjust(time),
    postRow: withPlatformAdjust(postRow),
    lo2d: withPlatformAdjust(lo2d),
    lo3d: withPlatformAdjust(lo3d),
    aGroup: withPlatformAdjust(aGroup),
    ...(afi ? { afi: withPlatformAdjust(afi) } : {}),
    ...(kpPostGroup ? { kpPostGroup: withPlatformAdjust(kpPostGroup) } : {}),
    ...(kpLoGroup ? { kpLoGroup: withPlatformAdjust(kpLoGroup) } : {}),
});

const DEFAULT_TEXT_STYLE = createTextStyle();

const MC_TEXT_STYLE = createTextStyle({
    date: withPlatformAdjust({
        fontSize: boxClamp(12, 5, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, MC_TOPBAR_PLATFORM_ADJUST),
    time: withPlatformAdjust({
        fontSize: boxClamp(12, 5, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, MC_TOPBAR_PLATFORM_ADJUST),
    postRow: withPlatformAdjust({
        twoDColor: COLORS.brightRed,
        threeDColor: COLORS.blue,
        fontSize: boxClamp(16, 8, 40),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MC_POST_PLATFORM_ADJUST),
    lo2d: withPlatformAdjust({
        textColor: COLORS.brightRed,
        fontSize: boxClamp(11, 5.2, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MC_LO_PLATFORM_ADJUST),
    lo3d: withPlatformAdjust({
        textColor: COLORS.blue,
        fontSize: boxClamp(11, 5.2, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MC_LO_PLATFORM_ADJUST),
    aGroup: withPlatformAdjust({
        twoDColor: COLORS.brightRed,
        threeDColor: COLORS.blue,
        fontSize: FONT_SIZES.large,
        twoDFontSize: boxClamp(13, 6.4, 32),
        threeDFontSize: boxClamp(14, 7.2, 36),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MC_A_GROUP_PLATFORM_ADJUST),
});

const MT_TEXT_STYLE = createTextStyle({
    date: withPlatformAdjust({
        fontSize: boxClamp(12, 5, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, MT_TOPBAR_PLATFORM_ADJUST),
    time: withPlatformAdjust({
        fontSize: boxClamp(12, 5, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, MT_TOPBAR_PLATFORM_ADJUST),
    postRow: withPlatformAdjust({
        twoDColor: COLORS.darkRed,
        threeDColor: COLORS.darkBlueNavy,
        fontSize: boxClamp(16, 8, 40),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MT_POST_PLATFORM_ADJUST),
    lo2d: withPlatformAdjust({
        textColor: COLORS.darkRed,
        fontSize: boxClamp(11, 5.2, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MT_LO_PLATFORM_ADJUST),
    lo3d: withPlatformAdjust({
        textColor: COLORS.darkBlueNavy,
        fontSize: boxClamp(11, 5.2, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MT_LO_PLATFORM_ADJUST),
    aGroup: withPlatformAdjust({
        twoDColor: COLORS.brightRed,
        threeDColor: COLORS.blue,
        fontSize: FONT_SIZES.large,
        twoDFontSize: boxClamp(13, 6.4, 32),
        threeDFontSize: boxClamp(14, 7.2, 36),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MT_A_GROUP_PLATFORM_ADJUST),
});

const MT_NIGHT_TEXT_STYLE = createTextStyle({
    date: withPlatformAdjust({
        fontSize: boxClamp(12, 5, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, MT_NIGHT_TOPBAR_PLATFORM_ADJUST),
    time: withPlatformAdjust({
        fontSize: boxClamp(12, 5, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, MT_NIGHT_TOPBAR_PLATFORM_ADJUST),
    postRow: withPlatformAdjust({
        twoDColor: COLORS.darkRed,
        threeDColor: COLORS.darkBlueNavy,
        fontSize: boxClamp(18, 9, 44),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MT_NIGHT_POST_PLATFORM_ADJUST),
    lo2d: withPlatformAdjust({
        textColor: COLORS.darkRed,
        fontSize: boxClamp(11, 5.2, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MT_NIGHT_LO_PLATFORM_ADJUST),
    lo3d: withPlatformAdjust({
        textColor: COLORS.darkBlueNavy,
        fontSize: boxClamp(11, 5.2, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MT_NIGHT_LO_PLATFORM_ADJUST),
    aGroup: withPlatformAdjust({
        twoDColor: COLORS.brightRed,
        threeDColor: COLORS.blue,
        fontSize: FONT_SIZES.large,
        twoDFontSize: boxClamp(14, 7, 34),
        threeDFontSize: boxClamp(13, 6.6, 32),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, MT_NIGHT_A_GROUP_PLATFORM_ADJUST),
});

const TC_TEXT_STYLE = createTextStyle({
    date: withPlatformAdjust({
        fontSize: boxClamp(12, 5, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, TC_TOPBAR_PLATFORM_ADJUST),
    time: withPlatformAdjust({
        fontSize: boxClamp(12, 5, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, TC_TOPBAR_PLATFORM_ADJUST),
    postRow: withPlatformAdjust({
        twoDColor: COLORS.red,
        threeDColor: COLORS.darkBlueNavy,
        fontSize: boxClamp(16, 8, 40),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, TC_POST_PLATFORM_ADJUST),
    lo2d: withPlatformAdjust({
        textColor: COLORS.red,
        fontSize: boxClamp(11, 5.2, 30),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, TC_LO_PLATFORM_ADJUST),
    lo3d: withPlatformAdjust({
        textColor: COLORS.darkBlueNavy,
        fontSize: boxClamp(11, 5.2, 30),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, TC_LO_PLATFORM_ADJUST),
    aGroup: withPlatformAdjust({
        twoDColor: COLORS.brightRed,
        threeDColor: COLORS.darkBlueNavy,
        fontSize: FONT_SIZES.large,
        twoDFontSize: boxClamp(13, 6.4, 32),
        threeDFontSize: boxClamp(14, 7.2, 36),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, TC_A_GROUP_PLATFORM_ADJUST),
});

const KH_TEXT_STYLE = createTextStyle({
    date: withPlatformAdjust({
        fontSize: boxClamp(10, 4.6, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, KH_TOPBAR_PLATFORM_ADJUST),
    time: withPlatformAdjust({
        fontSize: boxClamp(10, 4.6, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, KH_TOPBAR_PLATFORM_ADJUST),
    postRow: withPlatformAdjust({
        twoDColor: COLORS.black,
        threeDColor: COLORS.brightRed,
        fontSize: boxClamp(20, 10.2, 50),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, KH_MAIN_PLATFORM_ADJUST),
    lo2d: withPlatformAdjust({
        textColor: COLORS.black,
        fontSize: boxClamp(20, 10.2, 50),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, KH_MAIN_PLATFORM_ADJUST),
    lo3d: withPlatformAdjust({
        textColor: COLORS.brightRed,
        fontSize: boxClamp(20, 10.2, 50),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, KH_MAIN_PLATFORM_ADJUST),
});

const SC_TEXT_STYLE = createTextStyle({
    date: withPlatformAdjust({
        fontSize: boxClamp(12, 5.2, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, SC_TOPBAR_PLATFORM_ADJUST),
    time: withPlatformAdjust({
        fontSize: boxClamp(12, 5.2, 26),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 600,
    }, SC_TOPBAR_PLATFORM_ADJUST),
    postRow: withPlatformAdjust({
        twoDColor: COLORS.white,
        threeDColor: "#febf0f",
        fontSize: boxClamp(24, 12, 58),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, SC_MAIN_PLATFORM_ADJUST),
    lo2d: withPlatformAdjust({
        textColor: COLORS.blue,
        fontSize: boxClamp(24, 12, 58),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, SC_MAIN_PLATFORM_ADJUST),
    lo3d: withPlatformAdjust({
        textColor: COLORS.red,
        fontSize: boxClamp(24, 12, 58),
        fontFamily: VNVOGUE_FONT_FAMILY,
        fontWeight: 500,
    }, SC_MAIN_PLATFORM_ADJUST),
});

const LEAP_TEXT_STYLE = createTextStyle({
    date: {
        fontSize: boxClamp(10, 4.9, 26),
        fontFamily: '"CenturyPSProBold", "Times New Roman", Georgia, serif',
        fontWeight: 600,
        letterSpacing: "-0.015em",
        textShadow: "0 1px 0 rgba(255, 255, 255, 0.2)",
    },
    time: {
        fontSize: boxClamp(10, 4.9, 26),
        fontFamily: '"CenturyPSProBold", "Times New Roman", Georgia, serif',
        fontWeight: 600,
        letterSpacing: "-0.015em",
        textShadow: "0 1px 0 rgba(255, 255, 255, 0.2)",
    },
    postRow: {
        twoDColor: "#fc0300",
        threeDColor: "#022264",
        fourDColor: "#3e572c",
        fontSize: boxClamp(12, 5.8, 28),
        columnGap: "1%",
        fontFamily: '"CenturyPSProBold", "Times New Roman", Georgia, serif',
        fontWeight: 500,
        letterSpacing: "-0.02em",
        textShadow: "0 1px 0 rgba(255, 255, 255, 0.16)",
    },
    lo2d: {
        textColor: "#fc0300",
        fontSize: boxClamp(10, 4.8, 24),
        fontFamily: '"CenturyPSProBold", "Times New Roman", Georgia, serif',
        fontWeight: 500,
        lineHeight: 1,
        letterSpacing: "-0.02em",
        textShadow: "0 1px 0 rgba(255, 255, 255, 0.16)",
    },
    lo3d: {
        textColor: "#022264",
        fontSize: boxClamp(10, 4.8, 24),
        fontFamily: '"CenturyPSProBold", "Times New Roman", Georgia, serif',
        fontWeight: 500,
        letterSpacing: "-0.02em",
        textShadow: "0 1px 0 rgba(255, 255, 255, 0.16)",
    },
});

const KP_DAY_TEXT_STYLE = createTextStyle({
    date: withPlatformAdjust({ fontSize: boxClamp(10, 4.8, 26), fontWeight: 500 }, KP_DAY_PLATFORM_ADJUST.topbar),
    time: withPlatformAdjust({ fontSize: boxClamp(10, 4.8, 26), fontWeight: 500 }, KP_DAY_PLATFORM_ADJUST.topbar),
    afi: withPlatformAdjust({
        twoDColor: COLORS.white,
        threeDColor: COLORS.white,
        fontSize: boxClamp(13, 6.4, 32),
        fontWeight: 500,
    }, KP_DAY_PLATFORM_ADJUST.afi),
    kpPostGroup: withPlatformAdjust({
        twoDColor: COLORS.white,
        threeDColor: COLORS.white,
        fontFamily: 'Arial, sans-serif',
        fontSize: boxClamp(10, 4.8, 26),
        fontWeight: 600,
    }, KP_DAY_PLATFORM_ADJUST.postGroup),
    kpLoGroup: withPlatformAdjust({
        twoDColor: COLORS.white,
        threeDColor: COLORS.white,
        fontSize: FONT_PRESETS.lg,
        fontFamily: 'Arial, sans-serif',
        fontWeight: 400,
    }, KP_DAY_PLATFORM_ADJUST.loGroup),
});

const KP_NIGHT_TEXT_STYLE = createTextStyle({
    date: withPlatformAdjust({ fontSize: boxClamp(10, 5.2, 26), fontWeight: 600 }, KP_NIGHT_PLATFORM_ADJUST.topbar),
    time: withPlatformAdjust({ fontSize: boxClamp(10, 5.2, 26), fontWeight: 600 }, KP_NIGHT_PLATFORM_ADJUST.topbar),
    postRow: withPlatformAdjust({
        twoDColor: COLORS.white,
        threeDColor: COLORS.white,
        fontSize: boxClamp(16, 8, 40),
        fontWeight: 500,
    }, KP_NIGHT_PLATFORM_ADJUST.post),
    lo2d: withPlatformAdjust({ textColor: COLORS.brightRed, fontSize: boxClamp(11, 5.2, 26), fontWeight: 500 }, KP_NIGHT_PLATFORM_ADJUST.lo),
    lo3d: withPlatformAdjust({ textColor: COLORS.brightRed, fontSize: boxClamp(11, 5.2, 26), fontWeight: 500 }, KP_NIGHT_PLATFORM_ADJUST.lo),
    aGroup: withPlatformAdjust({
        twoDColor: COLORS.black,
        threeDColor: COLORS.black,
        fontSize: FONT_SIZES.large,
        twoDFontSize: boxClamp(12, 6, 28),
        threeDFontSize: boxClamp(12, 6, 28),
        fontWeight: 500,
    }, KP_NIGHT_PLATFORM_ADJUST.aGroup),
});

export const TEXT_STYLES = {
    default: DEFAULT_TEXT_STYLE,
    mc: MC_TEXT_STYLE,
    mt: MT_TEXT_STYLE,
    mtNight: MT_NIGHT_TEXT_STYLE,
    tc: TC_TEXT_STYLE,
    kh: KH_TEXT_STYLE,
    sc: SC_TEXT_STYLE,
    leap: LEAP_TEXT_STYLE,
    kpDay: KP_DAY_TEXT_STYLE,
    kpNight: KP_NIGHT_TEXT_STYLE,
};
